import { Inject, OnlyInstantiableByContainer, Singleton } from "typescript-ioc";
import { UiComponent } from "Ui/Scripts/UiComponent";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";

import "../Styles/deliverymessage.css";
import { IDeliveryModel } from "./IDeliveryModel";

@OnlyInstantiableByContainer
@Singleton
export class HintMessage extends UiComponent {
    private readonly DATA_DELIVERY_CHANNEL: string = "data-delivery-channel";
    private readonly DEFAULT_CHANNEL: string = ".js-default-channel";
    private readonly SELECTED_ADDRESS_ID_DATA_KEY: string = "selectedAddressId";
    private readonly DATA_ADDRESS: string = "data-address";
    private readonly SELECTED: string = ".selected";
    private readonly MESSAGE_SELECTOR: string = "delivery-message";
    private readonly MESSAGE_TEXT_SELECTOR: string = "delivery-message__item";
    private readonly MESSAGE_TEXT_PLACEHOLDER: string = "message-text-placeholder";
    private readonly SHIPPING_OFFER_CLASS: string = "js-shipping-offer";

    protected _model: IDeliveryModel;

    private _messageMarkup: string = `
        <div class="delivery-message hidden">
            <div class="delivery-message__icon v-icon-info-outlined"></div>
            <div class="delivery-message__item">${this.MESSAGE_TEXT_PLACEHOLDER}</div>
        </div>
    `;

    public get key(): string {
        return "HintMessage";
    }

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory
    ) {
        super(componentFactory, binder, loggerFactory);
    }

    public init(): void {
        this.update(this._model);
    }

    private _makeChannelSelector(selectedDeliveryChannel: IChannelsInfo): string {
        const channelSelectorArray = [
            `[${this.DATA_DELIVERY_CHANNEL}="${selectedDeliveryChannel.DeliveryChannelId}"]`,
        ];

        let isDefaultChannelSelected: boolean = false;
        const radioButton = kendo.getRadioButton(this.DEFAULT_CHANNEL);
        if (radioButton) {
            isDefaultChannelSelected = radioButton.value();
        }

        if (selectedDeliveryChannel.IsHomeDelivery && !isDefaultChannelSelected) {
            const selectedAddressId = this.getData(this.SELECTED_ADDRESS_ID_DATA_KEY);
            if (selectedAddressId) {
                channelSelectorArray.push(`[${this.DATA_ADDRESS}="${selectedAddressId}"]`);
            }
        } else {
            channelSelectorArray.push(" > ");
        }

        channelSelectorArray.push(this.SELECTED);
        return channelSelectorArray.join("");
    }

    public update(data: IDeliveryModel): void {
        if (!data.SelectedDeliveryChannel) {
            return;
        }

        const channelSelector = this._makeChannelSelector(data.SelectedDeliveryChannel);
        const channel = this.findElement(channelSelector);

        if (!channel) {
            return;
        }

        if (!data.Message?.Text) {
            // hide hint message
            this._checkAndHideMessage(channel);
        } else {
            // render or update hint message
            const messageTextElement = channel.getElementsByClassName(this.MESSAGE_TEXT_SELECTOR);

            if (messageTextElement.length) {
                messageTextElement[0].innerHTML = data.Message.Text;
                const messageElement = messageTextElement[0].parentElement;
                if (messageElement) {
                    messageElement.classList.remove(this.CLASS_HIDDEN);
                }
            } else {
                const formattedMessageText = this._messageMarkup.replace(
                    this.MESSAGE_TEXT_PLACEHOLDER,
                    data.Message.Text
                );

                // insert for shipping offer if it exists
                const radioDescriptionElements =
                    channel.getElementsByClassName("radio-description");

                const shippingOfferElement = channel.getElementsByClassName(
                    this.SHIPPING_OFFER_CLASS
                );

                if (radioDescriptionElements.length) {
                    radioDescriptionElements[0].insertAdjacentHTML(
                        "afterend",
                        formattedMessageText
                    );
                } else if (shippingOfferElement.length) {
                    shippingOfferElement[0].insertAdjacentHTML("beforebegin", formattedMessageText);
                } else {
                    channel.insertAdjacentHTML("beforeend", formattedMessageText);
                }

                const messageElement = channel.getElementsByClassName(this.MESSAGE_SELECTOR);
                if (messageElement.length) {
                    messageElement[0].classList.remove(this.CLASS_HIDDEN);
                }
            }
        }
    }

    private _checkAndHideMessage(channel: Element): void {
        const messageElement = channel.getElementsByClassName(this.MESSAGE_SELECTOR);
        if (messageElement.length) {
            messageElement[0].classList.add(this.CLASS_HIDDEN);
        }
    }
}
